// RulerTool — click-click tool for placing measurement rulers

import * as THREE from 'three'
import type { Tool, ToolContext } from '@/types/editor'
import type { ToolManager } from '../ToolManager'
import type { RulerManager } from '../RulerManager'
import { createLineMesh } from '../RulerManager'
import type { DimensionOverlay } from '@/ui/DimensionOverlay'
import { DPM } from '@/constants/coordinates'
import { getViewer, getToolMode } from '@/ui/state'
import { debug } from '@/utils/debug'

export class RulerTool implements Tool {
  readonly name = 'ruler'
  readonly cursor = 'crosshair'
  readonly hint = 'tool.hint.ruler'

  private toolManager: ToolManager
  private rulerManager: RulerManager
  private overlay: DimensionOverlay

  private startPoint: { x: number; y: number } | null = null
  private currentWorld: THREE.Vector3 | null = null

  private previewGroup: THREE.Group

  constructor(
    toolManager: ToolManager,
    scene: THREE.Scene,
    rulerManager: RulerManager,
    overlay: DimensionOverlay
  ) {
    this.toolManager = toolManager
    this.rulerManager = rulerManager
    this.overlay = overlay

    this.previewGroup = new THREE.Group()
    this.previewGroup.name = 'ruler-preview'
    scene.add(this.previewGroup)
  }

  activate(): void {
    this.startPoint = null
    this.currentWorld = null
    this.clearPreview()
    const selMgr = getViewer()?.getScene()?.getSelectionManager()
    selMgr?.deselect()
    selMgr?.clearHover()
    selMgr?.setEnabled(false)
    debug.log('RulerTool', 'Activated')
  }

  deactivate(): void {
    this.cancelDrawing()
    if (getToolMode() === 'pointer') {
      getViewer()?.getScene()?.getSelectionManager()?.setEnabled(true)
    }
    debug.log('RulerTool', 'Deactivated')
  }

  onMouseDown(ctx: ToolContext): void {
    if (ctx.event.button !== 0) return
    if (!ctx.worldPoint || !ctx.editorPoint) return

    if (!this.startPoint) {
      // First click — set start
      this.startPoint = { x: ctx.editorPoint.x, y: ctx.editorPoint.y }
      this.updatePreview()
      ctx.event.preventDefault()
      ctx.event.stopPropagation()
      debug.log('RulerTool', `Start point set at (${ctx.editorPoint.x}, ${ctx.editorPoint.y})`)
    } else {
      // Second click — place ruler if distance > 1 dot
      const dx = ctx.editorPoint.x - this.startPoint.x
      const dy = ctx.editorPoint.y - this.startPoint.y
      if (dx * dx + dy * dy < 1) {
        debug.log('RulerTool', 'Points too close, ignoring')
        return
      }

      this.rulerManager.addRuler(this.startPoint, { x: ctx.editorPoint.x, y: ctx.editorPoint.y })
      debug.log('RulerTool', `Ruler placed`)

      // Reset for next ruler — stay in ruler mode so user can place multiple
      this.startPoint = null
      this.currentWorld = null
      this.clearPreview()
      this.overlay.hideAll()
      ctx.event.preventDefault()
      ctx.event.stopPropagation()
    }
  }

  onMouseMove(ctx: ToolContext): void {
    if (!ctx.worldPoint) return
    this.currentWorld = ctx.worldPoint.clone()

    if (this.startPoint) {
      this.updatePreview()

      // Show live distance via DimensionOverlay
      const startWorldX = this.startPoint.x / DPM
      const startWorldZ = this.startPoint.y / DPM
      const dx = ctx.worldPoint.x - startWorldX
      const dz = ctx.worldPoint.z - startWorldZ
      const distance = Math.sqrt(dx * dx + dz * dz)
      const midX = (startWorldX + ctx.worldPoint.x) / 2
      const midZ = (startWorldZ + ctx.worldPoint.z) / 2
      this.overlay.showEdge(distance, midX, midZ, ctx.camera)
    }
  }

  onMouseUp(_ctx: ToolContext): void {
    // Click handling is in onMouseDown
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.startPoint) {
        // Drawing in progress — cancel the current ruler, stay in ruler mode
        this.cancelDrawing()
      } else if (this.rulerManager.getRulerCount() > 0) {
        // No drawing, rulers exist — clear all rulers, stay in ruler mode
        this.rulerManager.clearAll()
        debug.log('RulerTool', 'All rulers cleared')
      } else {
        // No drawing, no rulers — exit to select
        this.toolManager.setActiveTool('select')
      }
    }
  }

  dispose(): void {
    this.cancelDrawing()
    this.previewGroup.parent?.remove(this.previewGroup)
  }

  // --- Private ---

  private cancelDrawing(): void {
    this.startPoint = null
    this.currentWorld = null
    this.clearPreview()
    this.overlay.hideAll()
  }

  private updatePreview(): void {
    this.clearPreview()
    if (!this.startPoint) return

    const y = 0.05
    const startWorld = new THREE.Vector3(this.startPoint.x / DPM, y, this.startPoint.y / DPM)

    // Start dot
    const dotGeom = new THREE.SphereGeometry(0.05, 8, 8)
    const dotMat = new THREE.MeshBasicMaterial({ color: 0xff6b6b, depthTest: false })
    const dot = new THREE.Mesh(dotGeom, dotMat)
    dot.position.copy(startWorld)
    dot.renderOrder = 999
    this.previewGroup.add(dot)

    // Rubberband line to current mouse (mesh-based for visibility)
    if (this.currentWorld) {
      const endWorld = new THREE.Vector3(this.currentWorld.x, y, this.currentWorld.z)
      const lineMesh = createLineMesh(startWorld, endWorld, 0xff6b6b, 0.02)
      this.previewGroup.add(lineMesh)
    }
  }

  private clearPreview(): void {
    while (this.previewGroup.children.length > 0) {
      const child = this.previewGroup.children[0]
      this.previewGroup.remove(child)
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose()
        if (child.material instanceof THREE.Material) child.material.dispose()
      }
    }
  }
}
